import {useEffect, useState} from 'react'
import Modal from '../Modal'
import {Campaign, CampaignType, CreateOrUpdateCampaignModel, TransportType} from '../../redux/campaignTypes'
import InputField from '../InputField'
import Select from '../Select'
import DatePicker from "../DatePicker";
import useCampaignActions from "../../hooks/useCampaignActions";

interface Props {
  initialValue?: Campaign
  onCloseClick?: () => void
  visible: boolean
}

const emptyCampaign = (): CreateOrUpdateCampaignModel => {
  return {
    name: '',
    type: CampaignType.Basic,
    executionTime: '',
    transportType: TransportType.Notification,
  }
}

const CreateOrUpdateCampaignModal = ({initialValue, onCloseClick, visible}: Props) => {
  const [campaign, setCampaign] = useState<CreateOrUpdateCampaignModel>(emptyCampaign)
  const {createCampaign, updateCampaign} = useCampaignActions()

  useEffect(() => {
    if (initialValue) {
      const base = {
        name: initialValue.name,
        type: initialValue.type,
        executionTime: initialValue.executionTime,
        transportType: initialValue.transportType
      }

      if (initialValue.type === CampaignType.FreePayout) {
        setCampaign({
          ...base,
          type: CampaignType.FreePayout,
          numberOfFreePayouts: initialValue.numberOfFreePayouts
        })
      } else {
        setCampaign({
          ...base,
          type: CampaignType.Basic
        })
      }
    } else {
      setCampaign(emptyCampaign)
    }
  }, [initialValue, visible])

  const handleOnOk = () => {
    if (!!initialValue) {
      updateCampaign(initialValue.id, campaign);
    } else {
      createCampaign(campaign);
    }

    onCloseClick && onCloseClick()
  }

  const handleOnCancel = () => {
    onCloseClick && onCloseClick()
  }

  const handleOnChange = (propName: string, value: any) => {
    setCampaign(prev => ({...prev, [propName]: value}))
  }

  const getCampaignTypeOptions = () => {
    return Object.entries(CampaignType)
      .map(([key, value]) => {
        return {
          name: key,
          value: value
        }
      })
  }

  const getTransportTypeOptions = () => {
    return Object.entries(TransportType)
      .map(([key, value]) => {
        return {
          name: key,
          value: value
        }
      })
  }

  return (
    <Modal
      title={initialValue ? 'Edit campaign' : 'Create campaign'}
      show={visible}
      okButtonTitle='Save'
      cancelButtonTitle='Cancel'
      scrollable={false}
      onHide={() => handleOnCancel()}
      onOkClick={() => handleOnOk()}
      onCancelClick={() => handleOnCancel()}
    >
      {!initialValue &&
          <Select
              id='type'
              key='type'
              label='Campaign Type'
              options={getCampaignTypeOptions()}
              selectedValue={campaign.type}
              onSelect={(value) => handleOnChange('type', value as CampaignType)}
          />}

      <InputField
        id='name'
        label='Name'
        value={campaign.name}
        onChange={(event) => handleOnChange('name', event.target.value)}
        placeholder='My campaign'
      />
      <DatePicker
        id='executionTime'
        label='Execution time'
        includeTime={true}
        value={campaign.executionTime}
        onChange={value => handleOnChange('executionTime', value)}
      />
      <Select
        id='transportType'
        key='transportType'
        label='Transport Type'
        options={getTransportTypeOptions()}
        selectedValue={campaign.transportType}
        onSelect={(value) => handleOnChange('transportType', value as TransportType)}
      />

      {(campaign?.type === CampaignType.FreePayout) &&
          <InputField
              id='numberOfFreePayouts'
              value={('numberOfFreePayouts' in campaign ? campaign.numberOfFreePayouts : 0).toString()}
              onChange={(event) => handleOnChange('numberOfFreePayouts', parseInt(event.target.value))}
              label='Number of free payouts'
              placeholder='1'
          />
      }
    </Modal>
  )
}

export default CreateOrUpdateCampaignModal
